import { ValidationMessage } from "@/flokit/core/validation-message/validation-message";
import { useHtmlId } from "@/flokit/utils/html/use-html-id";
import { cx } from "class-variance-authority";
import { useCallback, useState } from "react";
import { useIsInModal } from "../../modals/is-in-modal-context";
import { useIsFormDisabled } from "../contexts/disabled-form-context";
import { InputDescription } from "../input-label/input-description";
import { InputLabel } from "../input-label/input-label";
import {
  DescribedBy,
  getCorrectHtmlAttributesForInput,
  LabelProp,
  normalizeLabelProp,
} from "../input-label/label-prop";
import { InputValidationProps, LabelledInputProps } from "../types";
import { _BaseSelect, _BaseSelectProps } from "./base";
import { _RefDivWrapper } from "./custom-components";

export type SelectProps<IsMulti extends boolean> = LabelledInputProps<
  // 'aria-describedby' already used by 'react-select' to connect placeholder to input
  Exclude<LabelProp, DescribedBy>
> &
  InputValidationProps<HTMLInputElement> &
  _BaseSelectProps<IsMulti> & {
    controls?: React.ReactNode;
    isInModal?: boolean;
    id?: string;
  };

export const Select = <IsMulti extends boolean>({
  isRequired,
  tooltip,
  isDisabled,
  shouldShowValidation,
  validationMessage,
  controls,
  isClearable,
  isSearchable,
  isInModal: callerProvidedIsInModal,
  ...props
}: SelectProps<IsMulti>) => {
  const isFormDisabled = useIsFormDisabled();

  const generatedId = useHtmlId();
  const inputId = props.id ?? generatedId;

  const descriptionId = useHtmlId();
  const labelMethod = normalizeLabelProp(props.label);

  const [menuPortalTarget, setMenuPortalTarget] =
    useState<HTMLDivElement | null>(null);
  const portalRefCallback = useCallback((node) => {
    if (node !== null) {
      setMenuPortalTarget(node);
    }
  }, []);

  const isInModalContext = useIsInModal();
  const isInModal = callerProvidedIsInModal ?? isInModalContext;

  const hasVisibleLabelContent = labelMethod?.type === "default" || !!controls;

  const disabled = isDisabled ?? isFormDisabled;

  return (
    <div className={cx("flex w-full flex-col gap-y-2")}>
      <_RefDivWrapper
        ref={isInModal ? portalRefCallback : undefined}
        className={cx("flex flex-col gap-y-2")}
      >
        {hasVisibleLabelContent ? (
          <div className={cx("flex items-center justify-between")}>
            {labelMethod?.type === "default" ||
            labelMethod?.type === "hidden" ? (
              <InputLabel
                idOfElementToBeLabeled={inputId}
                labelMethod={labelMethod}
                isRequired={isRequired}
                tooltip={tooltip}
                isDisabled={disabled}
                interactivity="interactive"
              />
            ) : null}
            {controls ?? null}
          </div>
        ) : // If label is hidden and there aren't any controls, we don't want it adding a <div> and messing with spacing
        labelMethod?.type === "hidden" ? (
          <InputLabel
            idOfElementToBeLabeled={inputId}
            labelMethod={labelMethod}
            isRequired={isRequired}
            tooltip={tooltip}
            isDisabled={disabled}
            interactivity="interactive"
          />
        ) : null}

        {props.description ? (
          <div className="-mt-1.5">
            <InputDescription id={descriptionId}>
              {props.description}
            </InputDescription>
          </div>
        ) : null}

        <_BaseSelect
          {...props}
          {...getCorrectHtmlAttributesForInput({
            labelProp: props.label,
            htmlId: inputId,
            elementIsNonInteractive: false,
            fieldIsRequired: !!isRequired,
          })}
          aria-describedby={props.description ? descriptionId : undefined}
          isClearable={isClearable ?? true}
          isSearchable={isSearchable ?? true}
          isDisabled={disabled}
          isRequired={isRequired}
          hasError={shouldShowValidation}
          menuPortalTarget={isInModal ? menuPortalTarget : undefined}
        />
      </_RefDivWrapper>
      <ValidationMessage
        shouldShowValidation={shouldShowValidation}
        validationMessage={validationMessage}
      />
    </div>
  );
};
